import { FONT, T, PETROL } from "../theme";
import type { Phase, QA } from "../types";

export function TimelineNode({ data }: { data: { phases: Phase[] } }) {
  const COLW = 320, GAPX = 44;
  return (
    <div style={{ fontFamily: FONT, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 32 }}>
      <div style={{ position: "relative", display: "flex", gap: GAPX }}>
        <div style={{ position: "absolute", top: 8, left: 8, right: 8, height: 2, background: T.line }} />
        {data.phases.map((p, i) => (
          <div key={i} style={{ width: COLW, position: "relative" }}>
            <div style={{ width: 18, height: 18, borderRadius: "50%", background: PETROL, border: `3px solid ${T.white}`, position: "relative", zIndex: 1 }} />
            <div style={{ marginTop: 16, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PETROL }}>{p.when}</div>
            <div style={{ marginTop: 6, fontSize: 18, fontWeight: 800, color: T.ink, letterSpacing: "-0.01em" }}>{p.title}</div>
            <ul style={{ marginTop: 10, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 7 }}>
              {p.items.map((it, j) => <li key={j} style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.4 }}>{it}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProcessStripNode({ data }: { data: { steps: string[]; current: number; label?: string } }) {
  return (
    <div style={{ fontFamily: FONT, width: 760, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PETROL, marginBottom: 16 }}>{data.label || "The marketing loop"}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {data.steps.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, padding: "6px 12px", borderRadius: 999, whiteSpace: "nowrap", color: i === data.current ? T.white : T.ink2, background: i === data.current ? PETROL : T.bg, border: `1px solid ${i === data.current ? PETROL : T.line}` }}>{i + 1}. {s}</span>
            {i < data.steps.length - 1 && <span style={{ color: T.ink2, fontSize: 14 }}>→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function IntakeCardNode({ data }: { data: { title: string; status: string; items: QA[] } }) {
  return (
    <div style={{ fontFamily: FONT, width: 760, background: T.white, border: `2px solid ${PETROL}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.12)", padding: 28 }}>
      <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.white, background: PETROL, borderRadius: 999, padding: "5px 12px", marginBottom: 14 }}>{data.status}</span>
      <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", marginBottom: 18 }}>{data.title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {data.items.map((qa, i) => (
          <div key={i}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, lineHeight: 1.4 }}>{i + 1}. {qa.q}</div>
            <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.45, color: qa.a ? T.ink2 : "#B0A79C", fontStyle: qa.a ? "normal" : "italic" }}>{qa.a || "— awaiting your answer —"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RevenueModelNode({ data }: { data: { title: string; note: string; headers: string[]; rows: string[][]; foot?: string; label?: string } }) {
  return (
    <div style={{ fontFamily: FONT, width: 820, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 28 }}>
      <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PETROL, background: "rgba(43,85,96,0.10)", borderRadius: 999, padding: "5px 12px", marginBottom: 14 }}>{data.label || "Revenue model"}</span>
      <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{data.title}</div>
      <div style={{ marginTop: 8, fontSize: 13.5, color: T.ink2, lineHeight: 1.45 }}>{data.note}</div>
      <table style={{ marginTop: 18, width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead><tr>{data.headers.map((h, i) => (<th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "8px 10px", borderBottom: `2px solid ${T.line}`, color: PETROL, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>{h}</th>))}</tr></thead>
        <tbody>{data.rows.map((r, ri) => (<tr key={ri}>{r.map((c, ci) => (<td key={ci} style={{ textAlign: ci === 0 ? "left" : "right", padding: "9px 10px", borderBottom: `1px solid ${T.line}`, color: ci === 0 ? T.ink : T.ink2, fontWeight: ci === 0 ? 700 : 400 }}>{c}</td>))}</tr>))}</tbody>
      </table>
      {data.foot && <div style={{ marginTop: 14, fontSize: 12.5, color: T.ink2, fontStyle: "italic", lineHeight: 1.4 }}>{data.foot}</div>}
    </div>
  );
}

